// communityProfile/view.js
import React from 'react';
import {
  Typography,
  MenuItem,
  CircularProgress,
  Box,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  ExitToApp as ExitIcon,
  Report as ReportIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import { API_BASE_URL } from 'config/api';

import {
  ProfileContainer,
  SectionCard,
  SectionCardContent,
  SectionTitle,
  StatusBadge,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  ActionButton,
  ActionButtonsContainer,
  VisibilityOption,
  VisibilityTitle,
  VisibilityDescription,
  AdmissionProgress,
  ProgressLabel,
  StyledLinearProgress,
} from './styled';

const CommunityProfileView = ({
  // Data props
  membership,
  community,
  formData,
  loading,
  saveLoading,
  successMessage,
  errorMessage,
  
  // Handler props
  onVisibilityChange,
  onSave,
  onLeaveGroup,
  onReportGroup,
  onCloseSnackbar,
}) => {
  const getStatusText = (status) => {
    const statusMap = {
      'AC': 'Active',
      'PP': 'Pending Approval',
      'IN': 'Inactive',
      'RE': 'Rejected',
      'BA': 'Banned',
    };
    return statusMap[status] || 'Unknown';
  };

  const getRoleText = (role) => {
    const roleMap = {
      'FO': 'Founder',
      'AD': 'Admin',
      'MA': 'Manager',
      'PA': 'Participant',
      'GU': 'Guest',
    };
    return roleMap[role] || 'Unknown';
  };

  const getVisibilityText = (visibility) => {
    const visibilityMap = {
      'AL': 'All',
      'GR': 'Group',
      'OG': 'Other Groups',
      'EV': 'Events Only',
      'NO': 'None',
    };
    return visibilityMap[visibility] || 'Unknown';
  };

  const getVisibilityOptions = () => [
    {
      value: 'AL',
      title: 'Visible to All',
      description: 'Your profile is visible to everyone who visits the community'
    },
    {
      value: 'GR',
      title: 'Group Members Only',
      description: 'Only members of this community can see your profile'
    },
    {
      value: 'OG',
      title: 'Other Group Members',
      description: 'Members from your other communities can see you here'
    },
    {
      value: 'EV',
      title: 'Events Only',
      description: 'You\'re only visible to people attending the same events'
    },
    {
      value: 'NO',
      title: 'Hidden',
      description: 'Your profile is not visible to other members'
    },
  ];

  // Helper function to get proper image URL
  const getImageUrl = (image) => {
    if (!image) return null;
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  if (loading) {
    return (
      <ProfileContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      {/* Membership Status */}
      <SectionCard>
        <SectionCardContent>
          <SectionTitle>
            <PersonIcon />
            Membership Status
          </SectionTitle>

          <InfoGrid>
            <InfoItem>
              <InfoLabel>Role</InfoLabel>
              <InfoValue>
                <StatusBadge role={membership?.role}>
                  {getRoleText(membership?.role)}
                </StatusBadge>
              </InfoValue>
            </InfoItem>
            
            <InfoItem>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>
                <StatusBadge status={membership?.status}>
                  {getStatusText(membership?.status)}
                </StatusBadge>
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          {/* Admission Progress for Pending Members */}
          {membership?.status === 'PP' && community?.requires_admission_questions && (
            <AdmissionProgress>
              <ProgressLabel>
                <span>Admission Questions Progress</span>
                <span>{Math.round(membership?.admission_completion_percentage || 0)}%</span>
              </ProgressLabel>
              <StyledLinearProgress 
                variant="determinate" 
                value={membership?.admission_completion_percentage || 0}
              />
              {!membership?.has_completed_admission_questions && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Complete all admission questions to be considered for approval
                </Typography>
              )}
            </AdmissionProgress>
          )}
        </SectionCardContent>
      </SectionCard>

      {/* Privacy Settings */}
      <SectionCard>
        <SectionCardContent>
          <SectionTitle>
            <VisibilityIcon />
            Privacy Settings
          </SectionTitle>

          <Typography variant="body1" color="textSecondary" gutterBottom>
            Choose who can see your profile within this community:
          </Typography>

          {getVisibilityOptions().map((option) => (
            <VisibilityOption
              key={option.value}
              className={formData.visibility === option.value ? 'selected' : ''}
              onClick={() => onVisibilityChange(option.value)}
            >
              <VisibilityTitle>{option.title}</VisibilityTitle>
              <VisibilityDescription>{option.description}</VisibilityDescription>
            </VisibilityOption>
          ))}
        </SectionCardContent>
      </SectionCard>

      {/* Actions */}
      <ActionButtonsContainer>
        <Box display="flex" gap={2}>
          <ActionButton variant="report" onClick={onReportGroup}>
            <ReportIcon sx={{ mr: 1 }} />
            Report Community
          </ActionButton>
          
          <ActionButton variant="danger" onClick={onLeaveGroup}>
            <ExitIcon sx={{ mr: 1 }} />
            Leave Community
          </ActionButton>
        </Box>

        <ActionButton 
          variant="save" 
          onClick={onSave}
          disabled={saveLoading}
        >
          {saveLoading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <SaveIcon sx={{ mr: 1 }} />
          )}
          {saveLoading ? 'Saving...' : 'Save Changes'}
        </ActionButton>
      </ActionButtonsContainer>

      {/* Success/Error Messages */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={onCloseSnackbar} 
          severity="success" 
          variant="filled"
          icon={<CheckCircleIcon />}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={8000} 
        onClose={onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={onCloseSnackbar} 
          severity="error" 
          variant="filled"
          icon={<ErrorIcon />}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </ProfileContainer>
  );
};

export default CommunityProfileView;